import React from "react";
import { Box, Typography, IconButton, Link as MuiLink } from "@mui/material";
import { useTranslation } from "react-i18next";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DragIndicator from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const CategoryProductItemSortable = ({
  product,
  productId,
  defaultLanguage,
  onRemove,
  products,
  currencyCode = "",
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = defaultLanguage || i18n.language;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: productId });

  // If product doesn't exist, don't render the item
  if (!product) {
    return null;
  }

  // If product is already a full object, use it directly
  // If it's just an ID, find the full product from the products array
  const fullProductDetails =
    typeof product === "object"
      ? product
      : products.find((p) => p._id === product);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onRemove(productId);
  };

  const openProductInNewTab = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (productId) {
      window.open(
        `/customer/dsok/products/${productId}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const productName =
    fullProductDetails?.productName?.[currentLanguage] ||
    fullProductDetails?.name ||
    "Unnamed Product";

  const displayId = fullProductDetails?._id ?? productId;
  const priceNum = Number(fullProductDetails?.price);
  const hasPrice = !Number.isNaN(priceNum);
  const priceText = hasPrice ? priceNum.toFixed(2) : "—";

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        p: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "grab",
        "&:active": {
          cursor: "grabbing",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flex: 1,
        }}
        {...attributes}
        {...listeners}
      >
        <DragIndicator sx={{ color: "text.secondary", fontSize: 20 }} />
        <MuiLink
          component="button"
          variant="body1"
          onClick={openProductInNewTab}
          sx={{
            cursor: productId ? "pointer" : "default",
            textDecoration: "none",
            color: "primary.main",
            "&:hover": {
              textDecoration: productId ? "underline" : "none",
            },
          }}
        >
          {productName}
        </MuiLink>
        {productId && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1, fontSize: "0.75rem" }}
          >
            (
            <MuiLink
              component="button"
              variant="caption"
              onClick={openProductInNewTab}
              sx={{
                cursor: productId ? "pointer" : "default",
                textDecoration: "none",
                color: "text.secondary",
                fontFamily: "monospace",
                "&:hover": {
                  textDecoration: productId ? "underline" : "none",
                  color: "primary.main",
                },
              }}
            >
              {displayId}
            </MuiLink>
            )
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {priceText}{currencyCode ? ` ${currencyCode}` : ""}
        </Typography>
      </Box>
      <IconButton size="small" onClick={handleRemove}>
        <DeleteForeverIcon />
      </IconButton>
    </Box>
  );
};

export default CategoryProductItemSortable;
